import { useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Testimonial {
  author: string;
  city: string;
  text: string;
  rating: number;
  date: string;
}

interface TestimonialsCarouselProps {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
}

export const TestimonialsCarousel = ({ testimonials, title = "Avis clients", subtitle }: TestimonialsCarouselProps) => {
  const isMobile = useIsMobile();
  const [index, setIndex] = useState(0);

  const perPage = isMobile ? 1 : 3;
  const total = testimonials.length;
  const prev = () => setIndex(i => (i - 1 + total) % total);
  const next = () => setIndex(i => (i + 1) % total);
  const visible = Array.from({ length: perPage }, (_, k) => testimonials[(index + k) % total]);

  if (total === 0) return null;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-3">
            {title}
          </h2>
          {subtitle && (
            <p className="font-body text-muted-foreground">{subtitle}</p>
          )}
        </motion.div>

        <div className="relative max-w-5xl mx-auto flex items-center gap-2">
          <button
            onClick={prev}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-autosur-navy hover:bg-autosur-blue/5 transition-colors"
            aria-label="Avis précédents"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-6 overflow-hidden">
            {visible.map((t, i) => (
              <motion.div
                key={`${index}-${i}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-body text-autosur-navy text-sm mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-autosur-blue/10 flex items-center justify-center">
                    <span className="font-heading font-bold text-autosur-blue text-xs">{t.author.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-autosur-navy text-sm">{t.author}</p>
                    <p className="font-body text-muted-foreground text-xs">{t.city}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <button
            onClick={next}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-autosur-navy hover:bg-autosur-blue/5 transition-colors"
            aria-label="Avis suivants"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: total }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className="p-1"
              aria-label={`Aller à l'avis ${i + 1}`}
            >
              <span className={`block w-2.5 h-2.5 rounded-full transition-colors ${i === index ? 'bg-autosur-blue' : 'bg-gray-300'}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
